/**
 * Plan Store
 *
 * Zustand store for managing plan template state, CRUD operations, workout management,
 * and sharing functionality. Provides centralized state management for plans across the application.
 */

import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import { failureMessage } from './failure';
import { subscribeToPlanGenerated } from './job-progress.store';
import { createPersistStorage } from './persist-storage';
import { registerSessionScoped } from './session-scope';
import { planService } from '../services/plan.service';

import type {
  ID,
  SearchRequest,
  PaginationMeta,
  PlanTemplate,
  ShareResourceConfig,
} from '../types';
import type { FetchOptions } from './fetch-options';
import type { PlanTemplateInput } from '../validation/template.schemas';

// =============================================================================
// CONSTANTS
// =============================================================================

const STORAGE_KEY = 'personal-trainer-plan';
const MAX_RECENT_PLANS = 5;

// =============================================================================
// STORE INTERFACES
// =============================================================================

export interface PlanStoreState {
  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isFetching: boolean;
  isSharing: boolean;
  isActivating: boolean;

  // Error states
  error: string | null;
  createError: string | null;
  updateError: string | null;
  deleteError: string | null;
  fetchError: string | null;
  shareError: string | null;
  activateError: string | null;

  // Data state
  planTemplates: PlanTemplate[];
  plansPagination: PaginationMeta | null;
  recentPlans: PlanTemplate[];
  lastSearchParams: SearchRequest;
}

export interface PlanStoreActions {
  // Core CRUD operations
  /** Resolves whether the plan was created; a failure is in `createError`. */
  createPlan: (planData: PlanTemplateInput) => Promise<boolean>;
  fetchPlans: (
    searchParams?: SearchRequest,
    options?: FetchOptions
  ) => Promise<void>;
  /** Resolves whether the plan was updated; a failure is in `updateError`. */
  updatePlan: (planId: ID, data: PlanTemplateInput) => Promise<boolean>;
  deletePlan: (planId: ID) => Promise<boolean>;
  restorePlan: (planId: ID) => Promise<boolean>;

  // Recent plans
  addToRecentPlans: (plan: PlanTemplate) => void;

  /**
   * Plan activation, through the plan's own activate / deactivate endpoints.
   * A failure is kept in `activateError` rather than thrown. On success the
   * loaded list is re-read, since the active flag is on it too.
   */
  activatePlan: (plan: PlanTemplate) => Promise<void>;
  deactivatePlan: (plan: PlanTemplate) => Promise<void>;

  // Sharing
  sharePlan: (config: ShareResourceConfig) => Promise<void>;

  // Search
  setSearchParams: (params: SearchRequest) => void;
  clearSearch: () => Promise<void>;

  // State management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearAllErrors: () => void;
  reset: () => void;
}

export type PlanStore = PlanStoreState & PlanStoreActions;

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const planStore = createStore<PlanStore>()(
  persist(
    (set, get) => ({
      // =======================================================================
      // INITIAL STATE
      // =======================================================================

      // Loading states
      isLoading: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      isFetching: false,
      isSharing: false,
      isActivating: false,

      // Error states
      error: null,
      createError: null,
      updateError: null,
      deleteError: null,
      fetchError: null,
      shareError: null,
      activateError: null,

      // Data state
      planTemplates: [],
      plansPagination: null,
      recentPlans: [],
      lastSearchParams: {},

      // =======================================================================
      // CORE CRUD OPERATIONS
      // =======================================================================

      createPlan: async (planData: PlanTemplateInput): Promise<boolean> => {
        set({ isCreating: true, createError: null });

        try {
          const newPlan = await planService.create(planData);

          const { planTemplates } = get();
          set({ planTemplates: [newPlan, ...planTemplates] });
          return true;
        } catch (error) {
          const errorMessage = failureMessage(error, 'Failed to create plan');
          set({ createError: errorMessage });
          return false;
        } finally {
          set({ isCreating: false });
        }
      },

      fetchPlans: async (
        searchParams: SearchRequest = {},
        { fresh = false }: FetchOptions = {}
      ): Promise<void> => {
        if (fresh) planService.clearCache();
        set({
          isFetching: true,
          fetchError: null,
          lastSearchParams: searchParams,
        });

        try {
          // The whole list, never a page: the Plans page filters, counts and
          // picks the latest snapshot client-side, so a page would hide the rest.
          const paginatedResponse = await planService.getPlans({
            ...searchParams,
            paginate: false,
          });
          set({
            planTemplates: paginatedResponse.data,
            plansPagination: paginatedResponse.meta,
          });
        } catch (error) {
          const errorMessage = failureMessage(error, 'Failed to fetch plans');
          set({ fetchError: errorMessage });
        } finally {
          set({ isFetching: false });
        }
      },

      updatePlan: async (
        planId: ID,
        data: PlanTemplateInput
      ): Promise<boolean> => {
        set({ isUpdating: true, updateError: null });

        try {
          const updatedPlan = await planService.updatePlan(planId, data);
          const { planTemplates } = get();
          const updatedTemplates = planTemplates.map((plan) =>
            plan.id === planId ? updatedPlan : plan
          );

          set({ planTemplates: updatedTemplates });
          return true;
        } catch (error) {
          const errorMessage = failureMessage(error, 'Failed to update plan');
          set({ updateError: errorMessage });
          return false;
        } finally {
          set({ isUpdating: false });
        }
      },

      deletePlan: async (planId: ID): Promise<boolean> => {
        set({ isDeleting: true, deleteError: null });

        try {
          await planService.deletePlan(planId);

          const { planTemplates } = get();
          const filteredTemplates = planTemplates.filter(
            (plan) => plan.id !== planId
          );

          set({ planTemplates: filteredTemplates });
          return true;
        } catch (error) {
          const errorMessage = failureMessage(error, 'Failed to delete plan');
          set({ deleteError: errorMessage });
          return false;
        } finally {
          set({ isDeleting: false });
        }
      },

      restorePlan: async (planId: ID): Promise<boolean> => {
        set({ isDeleting: true, deleteError: null });

        try {
          await planService.restorePlan(planId);

          // Re-sync from the server so the restored plan reappears with its
          // canonical position and fields (single source of truth).
          const { lastSearchParams, fetchPlans } = get();
          await fetchPlans(lastSearchParams);
          return true;
        } catch (error) {
          const errorMessage = failureMessage(error, 'Failed to restore plan');
          set({ deleteError: errorMessage });
          return false;
        } finally {
          set({ isDeleting: false });
        }
      },

      // =======================================================================
      // RECENT PLANS
      // =======================================================================

      addToRecentPlans: (plan: PlanTemplate): void => {
        const { recentPlans } = get();
        const filtered = recentPlans.filter((p) => p.id !== plan.id);
        const updated = [plan, ...filtered].slice(0, MAX_RECENT_PLANS);
        set({ recentPlans: updated });
      },

      // =======================================================================
      // PLAN ACTIVATION
      // =======================================================================

      activatePlan: async (plan: PlanTemplate): Promise<void> => {
        set({ isActivating: true, activateError: null });

        try {
          await planService.activatePlan(plan.id);
          // Re-read the loaded list: the active flag is on it too. A deep link
          // to the plan never loaded one, and has nothing to re-read.
          const { planTemplates, lastSearchParams, fetchPlans } = get();
          if (planTemplates.length > 0) {
            await fetchPlans(lastSearchParams);
          }
        } catch (error) {
          const errorMessage = failureMessage(error, 'Failed to activate plan');
          set({ activateError: errorMessage });
        } finally {
          set({ isActivating: false });
        }
      },

      deactivatePlan: async (plan: PlanTemplate): Promise<void> => {
        set({ isActivating: true, activateError: null });

        try {
          await planService.deactivatePlan(plan.id);
          // Re-read the loaded list: the active flag is on it too. A deep link
          // to the plan never loaded one, and has nothing to re-read.
          const { planTemplates, lastSearchParams, fetchPlans } = get();
          if (planTemplates.length > 0) {
            await fetchPlans(lastSearchParams);
          }
        } catch (error) {
          const errorMessage = failureMessage(
            error,
            'Failed to deactivate plan'
          );
          set({ activateError: errorMessage });
        } finally {
          set({ isActivating: false });
        }
      },

      // =======================================================================
      // SHARING
      // =======================================================================

      sharePlan: async (config: ShareResourceConfig): Promise<void> => {
        set({ isSharing: true, shareError: null });

        try {
          await planService.sharePlan(config);
        } catch (error) {
          const errorMessage = failureMessage(error, 'Failed to share plan');
          set({ shareError: errorMessage });
        } finally {
          set({ isSharing: false });
        }
      },

      // =======================================================================
      // SEARCH ACTIONS
      // =======================================================================

      setSearchParams: (params: SearchRequest): void => {
        set({ lastSearchParams: params });
      },

      clearSearch: async (): Promise<void> => {
        const { fetchPlans } = get();
        set({ lastSearchParams: {} });
        await fetchPlans({});
      },

      // =======================================================================
      // STATE MANAGEMENT ACTIONS
      // =======================================================================

      setLoading: (loading: boolean): void => {
        set({ isLoading: loading });
      },

      setError: (error: string | null): void => {
        set({ error });
      },

      clearError: (): void => {
        set({ error: null });
      },

      clearAllErrors: (): void => {
        set({
          error: null,
          createError: null,
          updateError: null,
          deleteError: null,
          fetchError: null,
          shareError: null,
          activateError: null,
        });
      },

      reset: (): void => {
        set({
          // Loading states
          isLoading: false,
          isCreating: false,
          isUpdating: false,
          isDeleting: false,
          isFetching: false,
          isSharing: false,
          isActivating: false,

          // Error states
          error: null,
          createError: null,
          updateError: null,
          deleteError: null,
          fetchError: null,
          shareError: null,
          activateError: null,

          // Data state. `recentPlans` too: it is persisted, but it is the
          // account's, and reset runs at the session boundary.
          planTemplates: [],
          plansPagination: null,
          recentPlans: [],
          lastSearchParams: {},
        });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createPersistStorage(),
      partialize: (state) => ({
        recentPlans: state.recentPlans,
      }),
      version: 1,
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

// Loading selectors
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectPlanLoading = (state: PlanStore) => ({
  isLoading: state.isLoading,
  isCreating: state.isCreating,
  isUpdating: state.isUpdating,
  isDeleting: state.isDeleting,
  isFetching: state.isFetching,
  isSharing: state.isSharing,
  isActivating: state.isActivating,
});

// Error selectors
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const selectPlanErrors = (state: PlanStore) => ({
  error: state.error,
  createError: state.createError,
  updateError: state.updateError,
  deleteError: state.deleteError,
  fetchError: state.fetchError,
  shareError: state.shareError,
  activateError: state.activateError,
});

// Data selectors
export const selectPlanTemplates = (state: PlanStore): PlanTemplate[] =>
  state.planTemplates;

export const selectPlansPagination = (
  state: PlanStore
): PaginationMeta | null => state.plansPagination;

export const selectRecentPlans = (state: PlanStore): PlanTemplate[] =>
  state.recentPlans;

// Computed selectors
export const selectPlanById =
  (planId: ID) =>
  (state: PlanStore): PlanTemplate | undefined =>
    state.planTemplates.find((plan) => plan.id === planId);

export const selectActivePlans = (state: PlanStore): PlanTemplate[] =>
  state.planTemplates.filter((plan) => plan.active);

export const selectInactivePlans = (state: PlanStore): PlanTemplate[] =>
  state.planTemplates.filter((plan) => !plan.active);

export const selectPlansByDifficulty =
  (difficulty: string) =>
  (state: PlanStore): PlanTemplate[] =>
    state.planTemplates.filter((plan) => plan.difficulty === difficulty);

// Count selector
export const selectTotalPlans = (state: PlanStore): number =>
  state.plansPagination?.total ?? 0;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export const getPlanTemplates = (): PlanTemplate[] =>
  planStore.getState().planTemplates;

export const getRecentPlans = (): PlanTemplate[] =>
  planStore.getState().recentPlans;

// =============================================================================
// PLAN GENERATION
// =============================================================================

// A generated plan is made on the server, so nothing here invalidated the
// cached list: drop it, and read a list already loaded again so a mounted
// Plans screen shows the new plan.
subscribeToPlanGenerated(() => {
  const { plansPagination, lastSearchParams, fetchPlans } =
    planStore.getState();
  if (plansPagination === null) {
    planService.clearCache();
    return;
  }
  void fetchPlans(lastSearchParams, { fresh: true });
});

// =============================================================================
// SESSION SCOPE
// =============================================================================

registerSessionScoped('planStore', () => {
  planStore.getState().reset();
});
